import { useEffect, useMemo, useState } from 'react';
import { Link, useOutletContext, useParams } from 'react-router-dom';
import { CalendarCheck, CalendarOff, ChevronLeft, ChevronRight, Pencil } from 'lucide-react';
import { Button } from '~/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '~/components/ui/card';
import { Checkbox } from '~/components/ui/checkbox';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '~/components/ui/dialog';
import { Input } from '~/components/ui/input';
import { Label } from '~/components/ui/label';
import type { AppLayoutContext } from '~/types';
import {
  blocksOnBoard, currentTermName, draftBoardIds, nextBoardIds,
} from './attendanceBoardSelection';
import {
  useAttendanceDay, useCreateSchoolClosedDay, useDeleteSchoolClosedDay, useUpdateAttendanceBoard,
  useUpdateSchoolClosedDay,
} from './useAttendanceDay';

function localISO(date: Date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function shiftDate(date: string, days: number) {
  const [year, month, day] = date.split('-').map(Number);
  return localISO(new Date(year, month - 1, day + days));
}

function longDate(date: string) {
  const [year, month, day] = date.split('-').map(Number);
  return new Date(year, month - 1, day).toLocaleDateString(undefined, {
    weekday: 'long', month: 'long', day: 'numeric', year: 'numeric',
  });
}

function shortDate(iso: string) {
  const [year, month, day] = iso.split('-').map(Number);
  return new Date(year, month - 1, day).toLocaleDateString(undefined, {
    month: '2-digit', day: '2-digit', year: 'numeric',
  });
}

function isoDate(value: unknown) {
  return String(value).slice(0, 10);
}

type ClosedDay = {
  id: string;
  name: string;
  occursOn: unknown;
  endsOn?: unknown;
  holidayKey?: string | null;
};

function ClosedDayDates({ day }: { day: ClosedDay }) {
  const start = isoDate(day.occursOn);
  const end = day.endsOn ? isoDate(day.endsOn) : null;
  if (!end) return <>{shortDate(start)}</>;
  return (
    <>
      <span className="block">{shortDate(start)}</span>
      <span className="block">– {shortDate(end)}</span>
    </>
  );
}

function ClosedDayRow({
  day,
  onEdit,
  onRemove,
}: {
  day: ClosedDay;
  onEdit: () => void;
  onRemove: () => void;
}) {
  return (
    <li className="col-span-3 grid grid-cols-subgrid items-center py-2 text-sm">
      <span className="min-w-0 truncate pl-3 font-medium">{day.name}</span>
      <span className="px-3 text-muted-foreground">
        <ClosedDayDates day={day} />
      </span>
      <div className="flex justify-end gap-1 px-1">
        <Button variant="ghost" size="icon" aria-label={`Edit ${day.name}`} onClick={onEdit}>
          <Pencil className="h-4 w-4" />
        </Button>
        <Button variant="ghost" size="sm" onClick={onRemove}>Remove</Button>
      </div>
    </li>
  );
}

function EditClosedDayDialog({
  day,
  open,
  saving,
  onOpenChange,
  onSave,
}: {
  day: ClosedDay | null;
  open: boolean;
  saving: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (input: { id: string; name: string; occursOn: string; endsOn: string | null }) => Promise<string | null>;
}) {
  const [name, setName] = useState('');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [range, setRange] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!day || !open) return;
    const startDate = isoDate(day.occursOn);
    const endDate = day.endsOn ? isoDate(day.endsOn) : '';
    setName(day.name);
    setStart(startDate);
    setEnd(endDate);
    setRange(Boolean(endDate));
    setError(null);
  }, [day, open]);

  const save = async () => {
    if (!day) return;
    if (!name.trim() || !start || (range && !end)) {
      setError(range
        ? 'Enter the name and the first and last day school is closed.'
        : 'Enter the name and the date school is closed.');
      return;
    }
    setError(await onSave({
      id: day.id,
      name: name.trim(),
      occursOn: start,
      endsOn: range ? end : null,
    }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit closed day</DialogTitle>
          <DialogDescription>
            Change the name, move the date, or turn this into a break that covers several days.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="edit-closed-name">Name</Label>
            <Input
              id="edit-closed-name"
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <Checkbox
              checked={range}
              onCheckedChange={(checked) => setRange(checked === true)}
            />
            Date range
          </label>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="edit-closed-start">{range ? 'First day' : 'Date'}</Label>
              <Input
                id="edit-closed-start"
                type="date"
                value={start}
                onChange={(event) => setStart(event.target.value)}
              />
            </div>
            {range && (
              <div className="space-y-2">
                <Label htmlFor="edit-closed-end">Last day</Label>
                <Input
                  id="edit-closed-end"
                  type="date"
                  value={end}
                  onChange={(event) => setEnd(event.target.value)}
                />
              </div>
            )}
          </div>
          {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button onClick={save} disabled={saving}>{saving ? 'Saving…' : 'Save'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function AttendanceDay() {
  const { schoolSlug, yearSlug } = useParams();
  const { school } = useOutletContext<AppLayoutContext>();
  const schoolYear = school?.schoolYears?.find((year) => year.slug === yearSlug);
  const [date, setDate] = useState(() => localISO(new Date()));
  const [holidayName, setHolidayName] = useState('');
  const [holidayDate, setHolidayDate] = useState('');
  const [holidayEnd, setHolidayEnd] = useState('');
  const [holidayRange, setHolidayRange] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [editingDay, setEditingDay] = useState<ClosedDay | null>(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [draftIds, setDraftIds] = useState<Set<string>>(() => new Set());
  const [saveError, setSaveError] = useState<string | null>(null);

  const { data, loading, error, refetch } = useAttendanceDay(schoolYear?.id, date);
  const [createClosedDay, { loading: creating }] = useCreateSchoolClosedDay();
  const [deleteClosedDay] = useDeleteSchoolClosedDay();
  const [updateClosedDay] = useUpdateSchoolClosedDay();
  const [updateBoard, { loading: savingBoard }] = useUpdateAttendanceBoard();

  const year = data?.schoolYear;
  const rolls = year?.attendanceRolls ?? [];
  const closedDays = year?.schoolClosedDays ?? [];
  const closedToday = closedDays.find((day) => {
    const start = String(day.occursOn).slice(0, 10);
    const end = day.endsOn ? String(day.endsOn).slice(0, 10) : start;
    return date >= start && date <= end;
  });
  const savedIds = year?.attendanceBoardBlockIds ?? null;
  const attendanceBlocks = year?.scheduleBlocks ?? [];
  const eligible = useMemo(
    () => blocksOnBoard(attendanceBlocks, date)
      .slice()
      .sort((a, b) => a.displayTitle.localeCompare(b.displayTitle)),
    [attendanceBlocks, date],
  );
  const termName = currentTermName(attendanceBlocks, date);

  const openPicker = () => {
    setDraftIds(draftBoardIds(eligible.map((block) => block.id), savedIds));
    setSaveError(null);
    setPickerOpen(true);
  };

  const saveBoard = async (ids: string[] | null) => {
    if (!schoolYear?.id) {
      setSaveError('The school year is still loading. Try again in a moment.');
      return;
    }
    setSaveError(null);
    try {
      const result = await updateBoard({
        variables: { input: { schoolYearId: schoolYear.id, scheduleBlockIds: ids } },
      });
      const userErrors = result.data?.updateAttendanceBoard?.userErrors ?? [];
      if (userErrors.length > 0) {
        setSaveError(userErrors.map((item) => item.message).join(' '));
        return;
      }
      if (!result.data?.updateAttendanceBoard) {
        setSaveError('The class list was not saved.');
        return;
      }
      setPickerOpen(false);
      await refetch();
    } catch (caught) {
      setSaveError(caught instanceof Error ? caught.message : 'The class list was not saved.');
    }
  };

  const saveDraft = () => {
    const ids = nextBoardIds({
      allIds: attendanceBlocks.filter((block) => block.attendanceEnabled).map((block) => block.id),
      visibleIds: eligible.map((block) => block.id),
      draftIds,
      savedIds,
    });
    return saveBoard(ids);
  };

  const addHoliday = async () => {
    if (!schoolYear?.id) return;
    const name = holidayName.trim();
    if (!name || !holidayDate || (holidayRange && !holidayEnd)) {
      setFormError(holidayRange
        ? 'Enter the name and the first and last day school is closed.'
        : 'Enter the holiday name and the date school is closed.');
      return;
    }
    setFormError(null);
    let result;
    try {
      result = await createClosedDay({
        variables: {
          input: {
            schoolYearId: schoolYear.id,
            occursOn: holidayDate,
            endsOn: holidayRange ? holidayEnd : null,
            name,
          },
        },
      });
    } catch (caught) {
      setFormError(caught instanceof Error ? caught.message : 'The closed day was not saved.');
      return;
    }
    const userErrors = result.data?.createSchoolClosedDay?.userErrors ?? [];
    if (userErrors.length > 0) {
      setFormError(userErrors.map((item) => item.message).join(' '));
      return;
    }
    setHolidayName('');
    setHolidayDate('');
    setHolidayEnd('');
    setHolidayRange(false);
    await refetch();
  };

  const removeHoliday = async (id: string) => {
    setFormError(null);
    const result = await deleteClosedDay({ variables: { input: { id } } });
    const userErrors = result.data?.deleteSchoolClosedDay?.userErrors ?? [];
    if (userErrors.length > 0) {
      setFormError(userErrors.map((item) => item.message).join(' '));
      return;
    }
    await refetch();
  };

  const saveClosedDay = async (input: { id: string; name: string; occursOn: string; endsOn: string | null }) => {
    setSavingEdit(true);
    try {
      const result = await updateClosedDay({ variables: { input } });
      const userErrors = result.data?.updateSchoolClosedDay?.userErrors ?? [];
      if (userErrors.length > 0) return userErrors.map((item) => item.message).join(' ');
      if (!result.data?.updateSchoolClosedDay) return 'The closed day was not saved.';
      setEditingDay(null);
      await refetch();
      return null;
    } catch (caught) {
      return caught instanceof Error ? caught.message : 'The closed day was not saved.';
    } finally {
      setSavingEdit(false);
    }
  };

  return (
    <div className="space-y-6 p-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold">Attendance</h1>
          <p className="text-muted-foreground">
            Take or correct attendance for the classes on this board.
          </p>
        </div>
        <Button asChild variant="outline">
          <Link to={`/app/${schoolSlug}/years/${yearSlug}/attendance-report`}>
            <CalendarCheck className="mr-1 h-4 w-4" /> Totals report
          </Link>
        </Button>
      </div>

      <Card>
        <CardContent className="flex flex-wrap items-center gap-2 pt-6">
          <Button variant="ghost" size="icon" aria-label="Previous day" onClick={() => setDate(shiftDate(date, -1))}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <div className="space-y-1">
            <Label htmlFor="attendance-day" className="sr-only">Date</Label>
            <Input id="attendance-day" type="date" value={date} onChange={(event) => setDate(event.target.value)} />
          </div>
          <Button variant="ghost" size="icon" aria-label="Next day" onClick={() => setDate(shiftDate(date, 1))}>
            <ChevronRight className="h-4 w-4" />
          </Button>
          <p className="text-sm text-muted-foreground">{longDate(date)}</p>
        </CardContent>
      </Card>

      {loading && <p className="text-sm text-muted-foreground">Loading rolls…</p>}
      {error && <div role="alert" className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">Unable to load attendance: {error.message}</div>}

      {closedToday && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CalendarOff className="h-5 w-5" /> No school · {closedToday.name}
            </CardTitle>
            <CardDescription>
              This date is closed, so no attendance roll is due.
            </CardDescription>
          </CardHeader>
        </Card>
      )}

      {!loading && !closedToday && (
        <Card>
          <CardHeader>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="space-y-1.5">
                <CardTitle>Rolls</CardTitle>
                <CardDescription>Open a roll to take it or to change marks that were already saved.</CardDescription>
              </div>
              <Button variant="outline" onClick={openPicker} disabled={eligible.length === 0}>
                Choose classes
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {rolls.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                {savedIds == null
                  ? 'No attendance is scheduled on this day.'
                  : savedIds.length === 0
                    ? 'No classes are selected. Choose which classes appear here.'
                    : 'None of the chosen classes meet on this day.'}
              </p>
            ) : (
              <div className="divide-y rounded-md border">
                {rolls.map((roll) => (
                  <div key={roll.scheduleBlock.id} className="flex flex-wrap items-center justify-between gap-3 p-3">
                    <div>
                      <p className="font-medium">{roll.scheduleBlock.displayTitle}</p>
                      <p className="text-sm text-muted-foreground">
                        {roll.timeLabel}
                        {roll.attendanceTaken
                          ? ` · Taken · ${roll.attendanceRecordedCount} of ${roll.expectedStudentCount}`
                          : ' · Not taken'}
                      </p>
                    </div>
                    <Button asChild size="sm" variant={roll.attendanceTaken ? 'outline' : 'default'}>
                      <Link to={`/app/${schoolSlug}/years/${yearSlug}/attendance/${roll.scheduleBlock.id}?date=${date}`}>
                        {roll.attendanceTaken ? 'Edit attendance' : 'Take attendance'}
                      </Link>
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <Dialog open={pickerOpen} onOpenChange={setPickerOpen}>
        <DialogContent className="sm:max-w-[480px]">
          <DialogHeader>
            <DialogTitle>Classes on this board</DialogTitle>
            <DialogDescription>
              {termName
                ? `${termName} only. Uncheck a class to take it off the rolls.`
                : 'Uncheck a class to take it off the rolls.'}
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[50vh] overflow-y-auto rounded-md border">
            {eligible.map((block) => (
              <label
                key={block.id}
                className="flex cursor-pointer items-center gap-3 border-b px-3 py-2.5 last:border-0 hover:bg-muted/30"
              >
                <Checkbox
                  checked={draftIds.has(block.id)}
                  onCheckedChange={(checked) => {
                    setDraftIds((current) => {
                      const next = new Set(current);
                      if (checked) next.add(block.id);
                      else next.delete(block.id);
                      return next;
                    });
                  }}
                />
                <span className="text-sm">{block.displayTitle}</span>
              </label>
            ))}
          </div>
          {saveError && <p role="alert" className="text-sm text-destructive">{saveError}</p>}
          <DialogFooter className="gap-2 sm:justify-between">
            <Button variant="ghost" onClick={() => saveBoard(null)} disabled={savingBoard}>
              Show every class
            </Button>
            <Button onClick={saveDraft} disabled={savingBoard}>
              {savingBoard ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Card>
        <CardHeader>
          <CardTitle>Days with no school</CardTitle>
          <CardDescription>
            Holidays repeat each school year. Use the pencil to rename a day, move it, or turn it into a break.
            The schedule skips these dates, and attendance is not expected.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-[minmax(0,1fr)_11rem_auto] items-start gap-x-3 gap-y-1">
            <Label htmlFor="holiday-name" className="pl-3">Name</Label>
            <Label htmlFor="holiday-date">Date</Label>
            <span aria-hidden="true" />
            <div className="min-w-0 space-y-2 pl-3">
              <Input
                id="holiday-name"
                value={holidayName}
                onChange={(event) => setHolidayName(event.target.value)}
                placeholder={holidayRange ? 'Christmas break' : 'Teacher workday'}
                className="min-w-0"
              />
              <label className="flex items-center gap-2 text-sm text-muted-foreground">
                <Checkbox
                  checked={holidayRange}
                  onCheckedChange={(checked) => setHolidayRange(checked === true)}
                />
                Date range
              </label>
            </div>
            <div className="space-y-2">
              <Input
                id="holiday-date"
                type="date"
                aria-label={holidayRange ? 'First day' : 'Date'}
                value={holidayDate}
                onChange={(event) => setHolidayDate(event.target.value)}
                className="w-full min-w-0"
              />
              {holidayRange && (
                <Input
                  id="holiday-end"
                  type="date"
                  aria-label="Last day"
                  value={holidayEnd}
                  onChange={(event) => setHolidayEnd(event.target.value)}
                  className="w-full min-w-0"
                />
              )}
            </div>
            <div className="flex justify-end px-3">
              <Button onClick={addHoliday} disabled={creating || !schoolYear?.id}>
                {creating ? 'Adding…' : 'Add closed day'}
              </Button>
            </div>
            {formError && <p role="alert" className="col-span-3 px-3 text-sm text-destructive">{formError}</p>}
            {closedDays.length === 0 ? (
              <p className="col-span-3 mt-3 px-3 text-sm text-muted-foreground">No closures yet. Weekdays on the schedule are still school days.</p>
            ) : (
              <ul className="col-span-3 mt-3 grid grid-cols-subgrid divide-y rounded-md border">
                {closedDays.map((day) => (
                  <ClosedDayRow
                    key={day.id}
                    day={day}
                    onEdit={() => setEditingDay(day)}
                    onRemove={() => removeHoliday(day.id)}
                  />
                ))}
              </ul>
            )}
          </div>
        </CardContent>
      </Card>

      <EditClosedDayDialog
        day={editingDay}
        open={editingDay !== null}
        saving={savingEdit}
        onOpenChange={(open) => { if (!open) setEditingDay(null); }}
        onSave={saveClosedDay}
      />
    </div>
  );
}
